import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, input, output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';
import {
  ButtonModule, ControlType, indexBy, SelectComponent, SimpleInputModule,
} from '@hglobal/common';
import {
  ShopOrder, ShopOrderForm, DeliveryMethod, FormType, ShopOrderPayload, PickupLocation, OrderUser,
  PickupOrderForm,
} from '@pt/models';
import { MfeConfig } from '@pt/mfe';
import { DictionariesService } from '@pt/services';

import {
  AcsComponent, DhlComponent, DpdComponent, FedexComponent, GapAkisExpressComponent,
  InpostComponent, InternationalComponent, NovaPoshtaComponent, PickupComponent, UkrposhtaComponent,
} from './delivery';

@Component({
  selector: 'pt-delivery-form',
  templateUrl: './delivery-form.component.html',
  styleUrls: ['./delivery-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    RouterLink,
    ButtonModule,
    SimpleInputModule,
    SelectComponent,
    PickupComponent,
    InternationalComponent,
    UkrposhtaComponent,
    NovaPoshtaComponent,
    FedexComponent,
    DhlComponent,
    DpdComponent,
    InpostComponent,
    AcsComponent,
    GapAkisExpressComponent,
  ],
})
export class DeliveryFormComponent {
  readonly #formBuilder = inject(FormBuilder);
  readonly #dictionaries = inject(DictionariesService);
  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #mfeConfig = inject(MfeConfig);

  order = input.required<ShopOrder>();
  user = input.required<OrderUser>();
  pickupLocations = input<PickupLocation[]>([]);
  cancelLink = input<string>('');
  cancelName = input<string>('');

  locationChanged = output<PickupLocation>();
  saveOrder = output<ShopOrderPayload>();

  readonly controlTypes = ControlType;
  readonly form: FormType<ShopOrderForm> = this.#formBuilder.group({
    deliveryMethod: ['', Validators.required],
    locationId: [null as number, Validators.required],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    phone: ['', Validators.required],
    comment: '',
  });

  deliveryMethods: DeliveryMethod[];

  get hasPickupFromOffice(): boolean {
    return !this.#mfeConfig || this.#mfeConfig.hasPickupFromOffice;
  }

  get isPaidInternationalShipping(): boolean {
    return this.form?.controls.deliveryMethod.value === 'international'
      && (!this.#mfeConfig || this.#mfeConfig.isPaidInternationalShipping);
  }

  readonly #formUpdateEffect = effect(() => {
    const user = this.user();
    const order = this.order();
    const prevLocationId = this.form.controls.locationId.value;

    if (order.location.id !== prevLocationId) {
      const deliveryIndex = indexBy(order.deliveryMethods, 'id');
      const deliveryMethods = this.#dictionaries.deliveryMethods;
      const [lastName, firstName] = user.fullName ? user.fullName.trim().split(/\s+/) : ['', ''];
      const prevDeliveryMethod = this.form.controls.deliveryMethod.value;

      this.deliveryMethods = deliveryMethods.filter(item => deliveryIndex[item.id]);

      this.form.patchValue({
        firstName,
        lastName,
        locationId: order.location.id,
        deliveryMethod: prevDeliveryMethod === 'pickup' || deliveryIndex[prevDeliveryMethod]
          ? prevDeliveryMethod
          : this.deliveryMethods[0].id,
        phone: user.phone || '',
      });
    }
  });

  readonly #deliveryMethodSub = this.form.controls.deliveryMethod.valueChanges
    .pipe(takeUntilDestroyed())
    .subscribe(deliveryMethod => {
      if (deliveryMethod === 'pickup') {
        this.form.controls.phone.removeValidators(Validators.required);
      } else {
        this.form.controls.phone.addValidators(Validators.required);
      }
      setTimeout(() => this.#changeDetectorRef.markForCheck()); // fix for form.invalid
    });

  setOffice(): void {
    this.form.controls.deliveryMethod.setValue('pickup');
  }

  setDelivery(): void {
    this.form.controls.deliveryMethod.setValue(this.deliveryMethods[0].id);
  }

  save(): void {
    const form = this.form.getRawValue();
    const products = this.order().products.map(product => ({
      id: product.id,
      quantity: product.quantity,
    }));

    form.locationId = (form as PickupOrderForm).delivery?.id || form.locationId;

    if (!form.phone) {
      delete form.phone;
    }

    this.saveOrder.emit({ products, form });
  }
}
